import React, { useState, useEffect } from 'react';
import BasePage from './BasePage';
import WeatherMap from '../components/WeatherMap';
import {
    Typography, Select, Spin, Space, Table, Tag, Empty, Card,
} from 'antd';
import {
    CloudOutlined, EnvironmentOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import { weatherService, sitesService } from '../services/api';
import { useAuth } from '../hooks/useAuth';

// ── Coordenadas fixas por site — NÃO ALTERAR ──────────────────────────────────
const COORDS_FIXAS = {
    fiap:    { lat: -23.57428, lon: -46.62329 },
    quarahy: { lat: -22.91456, lon: -43.22432 },
};

function resolveCoordinates(site) {
    const nome = (site?.name || '').toLowerCase();

    // 1. Sites conhecidos: coordenadas fixas com prioridade absoluta
    if (nome.includes('quarahy')) return Promise.resolve(COORDS_FIXAS.quarahy);
    if (nome.includes('fiap'))    return Promise.resolve(COORDS_FIXAS.fiap);

    // 2. Coordenadas do banco de dados
    const lat = parseFloat(site?.latitude);
    const lon = parseFloat(site?.longitude);
    if (!isNaN(lat) && !isNaN(lon) && lat !== 0 && lon !== 0) {
        return Promise.resolve({ lat, lon });
    }

    // 3. Fallback: São Paulo centro
    return Promise.resolve({ lat: -23.5505, lon: -46.6333 });
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const windDir = (deg) => {
    if (deg === undefined || deg === null) return '—';
    const dirs = ['N','NE','L','SE','S','SO','O','NO'];
    return dirs[Math.round(deg / 45) % 8];
};

const fmtDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
const fmtTime = (iso) => {
    const d = new Date(iso);
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const { Title, Text } = Typography;
const { Option } = Select;

// ── Componente ────────────────────────────────────────────────────────────────
const DashboardMeteorologico = () => {
    const { user } = useAuth();
    const [loadingSites,   setLoadingSites]   = useState(false);
    const [loadingWeather, setLoadingWeather] = useState(false);
    const [sites,          setSites]          = useState([]);
    const [selectedSite,   setSelectedSite]   = useState(null);   // null = nada selecionado
    const [weatherData,    setWeatherData]     = useState(null);
    const [forecastData,   setForecastData]    = useState(null);
    const [coords,         setCoords]          = useState(null);

    // Carrega lista de sites (sem auto-selecionar)
    useEffect(() => {
        const load = async () => {
            setLoadingSites(true);
            try {
                const empresaId = user?.empresaClienteId || null;
                const res = await sitesService.getAllWithRealtime(empresaId);
                if (res.success) setSites(res.data);
            } catch (_) {}
            finally { setLoadingSites(false); }
        };
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchWeather = async (site) => {
        setLoadingWeather(true);
        setWeatherData(null);
        setForecastData(null);
        try {
            const { lat, lon } = await resolveCoordinates(site);
            setCoords({ lat, lon });
            const [cur, fcast] = await Promise.all([
                weatherService.getCurrentWeather(lat, lon, site?.id, site?.name),
                weatherService.getForecast(lat, lon),
            ]);
            setWeatherData(cur);
            setForecastData(fcast);
        } catch (_) {}
        finally { setLoadingWeather(false); }
    };

    const handleSiteChange = (siteId) => {
        setSelectedSite(siteId);
        const site = sites.find(s => s.id === siteId);
        if (site) fetchWeather(site);
    };

    const siteData = sites.find(s => s.id === selectedSite);

    // ── Tabela de previsão ─────────────────────────────────────────────────────
    // Monta linhas: 1ª linha = dado atual + previsão 3h/3h do forecast
    const tableRows = React.useMemo(() => {
        if (!weatherData) return [];
        const rows = [];

        // Linha atual
        rows.push({
            key:       'current',
            isoDate:   new Date().toISOString(),
            date:      fmtDate(new Date()),
            time:      fmtTime(new Date()),
            temp:      weatherData.temperature?.current,
            feelsLike: weatherData.temperature?.feelsLike,
            humidity:  weatherData.humidity,
            pressure:  weatherData.pressure,
            windSpeed: weatherData.wind?.speed,
            windDir:   windDir(weatherData.wind?.deg),
            desc:      weatherData.weather?.description,
            isCurrent: true,
        });

        // Previsão 3h/3h
        if (forecastData?.forecast) {
            forecastData.forecast.forEach((item, idx) => {
                rows.push({
                    key:       `f-${idx}`,
                    date:      item.dateFormatted
                        ? item.dateFormatted.split(' ')[0]
                        : fmtDate(item.dt ? item.dt * 1000 : new Date()),
                    time:      item.dateFormatted
                        ? (item.dateFormatted.split(' ')[1] || '—')
                        : '—',
                    temp:      item.temperature?.max ?? item.temperature?.current,
                    feelsLike: item.temperature?.feelsLike ?? '—',
                    humidity:  item.humidity,
                    pressure:  item.pressure,
                    windSpeed: item.wind?.speed ?? '—',
                    windDir:   item.wind?.deg !== undefined ? windDir(item.wind.deg) : '—',
                    desc:      item.weather?.description,
                    isCurrent: false,
                });
            });
        }
        return rows;
    }, [weatherData, forecastData]);

    const columns = [
        {
            title: 'Data',
            dataIndex: 'date',
            key: 'date',
            width: 120,
            render: (v, r) => (
                <Text strong={r.isCurrent}>{v}</Text>
            ),
        },
        {
            title: 'Hora',
            dataIndex: 'time',
            key: 'time',
            width: 90,
            align: 'center',
            render: (v, r) => (
                <Text strong={r.isCurrent}>{v}</Text>
            ),
        },
        {
            title: 'Temperatura (°C)',
            dataIndex: 'temp',
            key: 'temp',
            width: 130,
            align: 'center',
            render: (v, r) => (
                <Text strong={r.isCurrent} style={{ color: r.isCurrent ? '#FB8500' : 'inherit', fontSize: r.isCurrent ? 15 : 14 }}>
                    {v !== undefined && v !== null ? `${v}°` : '—'}
                </Text>
            ),
        },
        {
            title: 'Sensação Térmica (°C)',
            dataIndex: 'feelsLike',
            key: 'feelsLike',
            width: 150,
            align: 'center',
            render: v => v !== undefined && v !== '—' ? `${v}°` : '—',
        },
        {
            title: 'Umidade (%)',
            dataIndex: 'humidity',
            key: 'humidity',
            width: 110,
            align: 'center',
            render: v => v !== undefined ? `${v}%` : '—',
        },
        {
            title: 'Pressão (hPa)',
            dataIndex: 'pressure',
            key: 'pressure',
            width: 120,
            align: 'center',
        },
        {
            title: 'Vento',
            key: 'vento',
            width: 100,
            align: 'center',
            render: (_, r) => r.windSpeed !== '—'
                ? <Text type="secondary">{r.windSpeed} m/s {r.windDir}</Text>
                : '—',
        },
        {
            title: 'Condição',
            dataIndex: 'desc',
            key: 'desc',
            render: (v, r) => v
                ? <Tag color={r.isCurrent ? 'orange' : 'default'} style={{ textTransform: 'capitalize' }}>{v}</Tag>
                : '—',
        },
    ];

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <BasePage>
            <div style={{ padding: '0 0 40px' }}>

                {/* Cabeçalho */}
                <div style={{
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', flexWrap: 'wrap',
                    gap: 16, marginBottom: 20,
                }}>
                    <Space align="center">
                        <div style={{
                            width: 44, height: 44, borderRadius: 10,
                            background: 'linear-gradient(135deg,#1890ff,#096dd9)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <CloudOutlined style={{ fontSize: 22, color: '#fff' }} />
                        </div>
                        <div>
                            <Title level={3} style={{ margin: 0 }}>Dados Meteorológicos</Title>
                            <Text type="secondary" style={{ fontSize: 13 }}>
                                Selecione uma usina para visualizar as condições climáticas
                            </Text>
                        </div>
                    </Space>

                    <Select
                        style={{ width: 420, minWidth: 260 }}
                        value={selectedSite}
                        onChange={handleSiteChange}
                        placeholder="Selecione uma usina..."
                        size="large"
                        showSearch
                        optionFilterProp="children"
                        loading={loadingSites}
                        allowClear
                        onClear={() => {
                            setSelectedSite(null);
                            setWeatherData(null);
                            setForecastData(null);
                            setCoords(null);
                        }}
                    >
                        {sites.map(s => (
                            <Option key={s.id} value={s.id}>
                                <EnvironmentOutlined style={{ marginRight: 6, color: '#FB8500' }} />
                                {s.name}
                            </Option>
                        ))}
                    </Select>
                </div>

                {/* Estado vazio */}
                {!selectedSite && !loadingWeather && (
                    <Empty
                        image={<CloudOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
                        imageStyle={{ height: 80 }}
                        description={
                            <Text type="secondary" style={{ fontSize: 15 }}>
                                Selecione uma usina acima para visualizar os dados meteorológicos
                            </Text>
                        }
                        style={{ marginTop: 80 }}
                    />
                )}

                {/* Loading */}
                {loadingWeather && (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
                        <Spin size="large" tip="Carregando dados meteorológicos..." />
                    </div>
                )}

                {/* Conteúdo principal */}
                {selectedSite && !loadingWeather && weatherData && (
                    <>
                        {/* Linha de resumo (minimalista — sem cards) */}
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: 24,
                            padding: '14px 20px', borderRadius: 10,
                            background: '#fff', border: '1px solid #f0f0f0',
                            marginBottom: 20, flexWrap: 'wrap',
                        }}>
                            <div>
                                <Text type="secondary" style={{ fontSize: 12 }}>Usina</Text>
                                <div>
                                    <Text strong style={{ fontSize: 15 }}>
                                        <EnvironmentOutlined style={{ color: '#FB8500', marginRight: 4 }} />
                                        {siteData?.name}
                                    </Text>
                                </div>
                            </div>
                            <div style={{ height: 36, width: 1, background: '#f0f0f0' }} />
                            <div>
                                <Text type="secondary" style={{ fontSize: 12 }}>Temperatura atual</Text>
                                <div>
                                    <Text strong style={{ fontSize: 22, color: '#FB8500' }}>
                                        {weatherData.temperature?.current}°C
                                    </Text>
                                    <Text type="secondary" style={{ marginLeft: 8, fontSize: 13 }}>
                                        {weatherData.weather?.description}
                                    </Text>
                                </div>
                            </div>
                            <div style={{ height: 36, width: 1, background: '#f0f0f0' }} />
                            <div>
                                <Text type="secondary" style={{ fontSize: 12 }}>Umidade</Text>
                                <div><Text strong style={{ fontSize: 15 }}>{weatherData.humidity}%</Text></div>
                            </div>
                            <div style={{ height: 36, width: 1, background: '#f0f0f0' }} />
                            <div>
                                <Text type="secondary" style={{ fontSize: 12 }}>Vento</Text>
                                <div>
                                    <Text strong style={{ fontSize: 15 }}>
                                        {weatherData.wind?.speed} m/s {windDir(weatherData.wind?.deg)}
                                    </Text>
                                </div>
                            </div>
                            <div style={{ height: 36, width: 1, background: '#f0f0f0' }} />
                            <div>
                                <Text type="secondary" style={{ fontSize: 12 }}>Pressão</Text>
                                <div><Text strong style={{ fontSize: 15 }}>{weatherData.pressure} hPa</Text></div>
                            </div>
                            {coords && (
                                <>
                                    <div style={{ height: 36, width: 1, background: '#f0f0f0' }} />
                                    <div>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Coordenadas</Text>
                                        <div>
                                            <Text style={{ fontSize: 12, color: '#8c8c8c' }}>
                                                {coords.lat.toFixed(5)}°, {coords.lon.toFixed(5)}°
                                            </Text>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Tabela de dados históricos/previsão */}
                        <Card
                            title={
                                <Space>
                                    <ThunderboltOutlined style={{ color: '#1890ff' }} />
                                    <Text strong>Histórico e Previsão — {siteData?.name}</Text>
                                </Space>
                            }
                            bodyStyle={{ padding: 0 }}
                            style={{ marginBottom: 20 }}
                        >
                            <Table
                                columns={columns}
                                dataSource={tableRows}
                                pagination={{ pageSize: 10, showTotal: t => `${t} registros` }}
                                size="middle"
                                rowClassName={r => r.isCurrent ? 'meteo-row-current' : ''}
                                scroll={{ x: 'max-content' }}
                            />
                        </Card>

                        {/* Mapa satélite preciso */}
                        {coords && (
                            <Card
                                title={
                                    <Space>
                                        <EnvironmentOutlined style={{ color: '#1890ff' }} />
                                        <Text strong>
                                            Localização — {siteData?.name}
                                        </Text>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            ({coords.lat.toFixed(5)}°, {coords.lon.toFixed(5)}°)
                                        </Text>
                                    </Space>
                                }
                                bodyStyle={{ padding: 0, overflow: 'hidden' }}
                                style={{ borderRadius: 8, marginBottom: 20 }}
                            >
                                <iframe
                                    key={`${coords.lat}-${coords.lon}`}
                                    title={`Mapa – ${siteData?.name}`}
                                    width="100%"
                                    height="340"
                                    frameBorder="0"
                                    style={{ border: 'none', display: 'block' }}
                                    src={`https://maps.google.com/maps?q=${coords.lat},${coords.lon}&t=k&z=18&output=embed&hl=pt-BR`}
                                    allowFullScreen
                                />
                            </Card>
                        )}

                        {/* Mapa meteorológico Windy */}
                        <Card
                            title={
                                <Space>
                                    <CloudOutlined style={{ color: '#1890ff' }} />
                                    <Text strong>Mapa Meteorológico (vento, chuva, nuvens)</Text>
                                </Space>
                            }
                            bodyStyle={{ padding: 0, overflow: 'hidden' }}
                            style={{ borderRadius: 8 }}
                        >
                            <WeatherMap
                                latitude={coords?.lat ?? weatherData.location?.lat}
                                longitude={coords?.lon ?? weatherData.location?.lon}
                                siteName={siteData?.name || 'Site'}
                                locationName={siteData?.name}
                                height={520}
                            />
                        </Card>
                    </>
                )}
            </div>

            {/* Destaque da linha atual */}
            <style>{`
                .meteo-row-current td {
                    background: #fff7e6 !important;
                    font-weight: 500;
                }
                .meteo-row-current:hover td {
                    background: #ffe7ba !important;
                }
            `}</style>
        </BasePage>
    );
};

export default DashboardMeteorologico;
